/**
 * アカウントの設定の画面（閉鎖）の日本語（en.account.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。
 * 表記の決め事は ja.ts の冒頭と同じ。
 */
import type { enAccount } from './en.account';
import type { PluralMsg } from './index';

type AccountMessages = { [K in keyof typeof enAccount]: (typeof enAccount)[K] extends string ? string : string | PluralMsg };

export const jaAccount: AccountMessages = {
  'account.topbarLink': 'アカウント',
  'account.eyebrow': 'あなたのアカウント',
  'account.title': 'アカウントの設定',
  'account.signedInAs': '{email} でログイン中',

  'account.close.title': 'アカウントの閉鎖',
  'account.close.lead': '閉鎖の前に、次の3つを必ず読んでください。閉鎖は {product} をもう使わないと決めたときのものです。',
  'account.close.leadLaunch': '閉鎖の前に、次の2つを必ず読んでください。閉鎖は {product} をもう使わないと決めたときのものです。',
  'account.close.p1Title': '1. 再開できません',
  'account.close.p1Body':
    '閉鎖したアカウントは再開できません（サポートに頼んでも戻せません）。また {product} を使うときは新しいアカウントを作ります（同じメールアドレスで作れるのは、このアカウントの情報が消された後です）。',
  'account.close.p2Title': '2. 前払いの残高は返されます',
  'account.close.p2Body':
    '使っていない前払いの残高は、今月の利用料が決まった後（遅くとも翌月3日ごろ）に返します。売上から入れた分は出金できる残高へ戻り、カードで入れた分はそのカードへ返金します（元の支払いの一部を返す形）。自動チャージも止まります。',
  'account.close.p2TitleLaunch': '2. 前払いの残高の扱い',
  'account.close.p2BodyLaunch':
    'カードで入れた前払いの残りは、その月の利用料が決まった後に、そのカードへ返金します（払っていない利用料があれば先に差し引きます。入金のときのカードの手数料は戻りません）。招待クレジットの残りは返金されず、閉鎖で消えます。自動チャージも止まります。',
  'account.close.p3Title': '3. 閉鎖後も出金できます（期限はありません）',
  'account.close.p3Body':
    '閉鎖した後もログインして売上を出金できます。{product} 側の期限はありませんが、長く引き出さずに置いておくと、米国の州の未請求財産として報告の対象になることがあります。',
  'account.close.afterTitle': '閉鎖した後',
  'account.close.afterCan': 'できること: 同じメールアドレスとパスワードでログイン、売上の確認、出金。',
  'account.close.afterCanLaunch': 'できること: 同じメールアドレスとパスワードでログイン。',
  'account.close.afterCannot': 'できないこと: 新しいプロジェクトを作ること。この画面を含め、すべての端末のダッシュボードからログアウトし、つないでいたAIエージェントやターミナルのコマンドもつながらなくなります。',
  'account.close.afterErase': '{product} が預かるお金が0になり返金がすべて済むと、メールアドレス・パスワード・名前は消されます。会計の記録は法律に従って残ります。',
  'account.close.aiNote': 'AIエージェントからはアカウントを閉鎖できません。閉鎖できるのはあなた本人だけです（この画面か、自分のパソコンのターミナルで {cmd}）。',

  'account.close.gamesTitle': 'まずプロジェクトを削除してください',
  'account.close.gamesBody':
    '閉鎖のついでに、動いているプロジェクトを{product}が消すことはしません。下のプロジェクトを1つずつ削除してから、アカウントを閉鎖してください（削除すると、そのアプリはすぐに誰も使えなくなります）。',
  'account.close.gamesNone': 'プロジェクトは残っていません。',
  'account.close.gamesRemainAlert': 'まだプロジェクトが残っています。先に上の一覧から削除してください。',
  'account.close.deleteGame': '削除…',
  'account.close.deleteGameTitle': '{name}を削除しますか？',
  'account.close.deleteGameBody': 'このアプリはすぐに誰も使えなくなります（アプリに書き込むキーも、サーバーだけで使うキーも使えなくなります）。元に戻せません。',
  'account.close.deleteGameSlug': '30日後に、このプロジェクトのURLの名前 {slug}（名前を変えていれば古い名前も）は、ほかの人が使えるようになります。Xなどに貼ったリンクは、その人のページに飛びます。',
  'account.close.typeSlug': '確認のため {slug} と入力してください',
  'account.close.deleteGameConfirm': 'プロジェクトを削除',

  'account.close.formTitle': 'アカウントを閉鎖する',
  'account.close.typeConfirm': 'メールアドレス（{email}）か {word} と入力してください',
  'account.close.password': 'パスワード',
  'account.close.submit': 'アカウントを閉鎖する（元に戻せません）',
  'account.close.wrongPassword': 'パスワードが違います。ログインはそのままです。もう一度入力してください。',

  'account.done.title': 'アカウントを閉鎖しました',
  'account.done.body': 'この画面を含め、すべての端末でログアウトしました。つないでいたAIエージェントやターミナルのコマンドもつながらなくなりました。',
  'account.done.withdraw': '同じメールアドレスとパスワードでログインすれば、売上を出金できます（お金 → 出金）。',
  'account.done.ok': 'OK',

  'account.closed.title': 'このアカウントは閉鎖されています',
  'account.closed.body': '{date}に閉鎖しました。ログインと売上の出金はできます。新しいプロジェクトは作れず、アカウントは再開できません。',
  'account.closed.bodyLaunch': '{date}に閉鎖しました。新しいプロジェクトは作れず、アカウントは再開できません。',
  'account.closed.withdraw': '出金へ',
  'account.closed.banner': 'このアカウントは閉鎖されています。売上の出金はできます。',
  'account.closed.bannerLaunch': 'このアカウントは閉鎖されています。新しいプロジェクトは作れません。',

  'error.account_closed': 'このアカウントは閉鎖されているため、新しいプロジェクトは作れません。売上の出金はできます。',
  'error.account_closedLaunch': 'このアカウントは閉鎖されているため、新しいプロジェクトは作れません。',
  'error.projects_remain': 'まだプロジェクトが残っています。先にプロジェクトを削除してから閉鎖してください。',
  'error.reauthentication_failed': 'パスワードが違います。'
};
